import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import { setBnlPreShootStudentsDetails } from '../../../actions/action_bnl_pre_shoot';
import {
  getShootData,
  formatAndSetBackgroundOptions,
  formatAndSetIncentiveProducts,
  setRetouchingProduct,
  setRetouchingProducts,
  setPersonalizationProduct,
  setInterstitialProducts,
  setPreCartOfferProducts,
  setTriggersPreCartOfferProducts,
} from '../../../actions/action_shoot';
import { setAdditionalData } from '../../../reducers/additionalDataSlice';
import { updateBnlUnlockedGroups } from '../../../actions/action_cart';

// React.js components
import Dropdown from '../../utils/dropdown';
import FormSkeletonLoader from '../../utils/FormSkeletonLoader';

// Services
import { getPrepayFormConfig } from '../../../services/prepay_form_service';

// constants files
import STUDIOS_REQUIRING_TEAM_NAMES from '../../../constants/studios_requiring_team_names';

const PreshootDetailsWrapper = styled.div`
  border-radius: 4px;
  border: 1px solid #ccc;
  background-color: #fff;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  padding: 24px;
  margin: 100px 0;
  text-align: center;
  @media (max-width: 767px) {
    padding: 16px;
  }
`;

class BnlParentStudentPreShootDetailForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      age: '',
      coachName: '',
      homeRoom: '',
      grade: '',
      leagueName: '',
      schoolName: '',
      teacherLastName: '',
      teacherSalutation: '',
      teacherName: '', // Added for config-driven forms
      teamName: '',
      useConfigDrivenForm: false,
      formConfig: null,
      loadingConfig: true, // Start with loading true since we'll fetch on mount
    };

    this.handleKeyPress = this.handleKeyPress.bind(this);
    this.handleOnChange = this.handleOnChange.bind(this);
    this.submitForm = this.submitForm.bind(this);
  }

  componentDidMount() {
    // Fetch shoot data on mount to get prepay form config
    this.fetchInitialShootData();
  }

  componentDidUpdate(prevProps) {
    // Check if shoot data has been loaded with prepay form config
    if (prevProps.shoot !== this.props.shoot) {
      this.checkForPrepayFormConfig();
    }
  }

  fetchInitialShootData() {
    const { bnlPreShoot } = this.props;

    // Only fetch if we have the shoot key
    if (bnlPreShoot && bnlPreShoot.shootKey) {
      // Use lightweight endpoint to just get prepay form config
      getPrepayFormConfig(bnlPreShoot.shootKey)
        .then((resp) => {
          if (resp.data && resp.data.prepay_form_config) {
            this.setState({
              formConfig: resp.data.prepay_form_config,
              useConfigDrivenForm:
                resp.data.prepay_form_config.enabled === true,
              loadingConfig: false,
            });

            // Also store event types if provided
            if (resp.data.event_type || resp.data.event_category) {
              this.props.setAdditionalData({
                eventType: resp.data.event_type,
                eventCategory: resp.data.event_category,
              });
            }
          } else {
            // No config available, use default form
            this.setState({
              loadingConfig: false,
            });
          }
        })
        .catch((error) => {
          console.error('Error fetching prepay form config:', error);
          // Error fetching, use default form
          this.setState({
            loadingConfig: false,
          });
        });
    } else {
      // Can't fetch without shoot key, use default form
      this.setState({
        loadingConfig: false,
      });
    }
  }

  checkForPrepayFormConfig() {
    // Get prepay form config from shoot data (passed from API)
    const { shoot } = this.props;
    if (shoot && shoot.prepay_form_config) {
      this.setState({
        formConfig: shoot.prepay_form_config,
        useConfigDrivenForm: shoot.prepay_form_config.enabled === true,
      });
    }
  }

  handleKeyPress(event) {
    if (event.key === 'Enter') {
      this.submitForm(event);
    }
  }

  handleOnChange(event) {
    this.setState({ [event.target.name]: event.target.value });
  }

  deepCloneObject(object) {
    // converts date objects to strings but not back to dates
    return JSON.parse(JSON.stringify(object));
  }

  cloneArray(array) {
    return array.slice();
  }

  formatBackgroundOptions(containsBackgroundCategories, backgroundOptions) {
    const formattedBackgroundOptions = {};

    if (containsBackgroundCategories) {
      backgroundOptions.forEach((backgroundSet) => {
        backgroundSet.background_images.forEach((backgroundOption) => {
          formattedBackgroundOptions[backgroundOption.id] = backgroundOption;
        });
      });
    } else {
      backgroundOptions.forEach((backgroundOption) => {
        formattedBackgroundOptions[backgroundOption.id] = backgroundOption;
      });
    }

    this.props.formatAndSetBackgroundOptions(formattedBackgroundOptions);
  }

  formatIncentiveProducts(incentiveProducts) {
    const formattedIncentiveProducts = {};

    incentiveProducts.forEach((incentiveProduct) => {
      if (!formattedIncentiveProducts[incentiveProduct.incentive_threshold]) {
        formattedIncentiveProducts[incentiveProduct.incentive_threshold] = [];
      }

      formattedIncentiveProducts[incentiveProduct.incentive_threshold].push(
        incentiveProduct,
      );
    });

    this.props.formatAndSetIncentiveProducts(formattedIncentiveProducts);
  }

  setUnlockedGroups(studentId) {
    const orderState = this.props.cart.orders[studentId];
    let bnlUnlockedGroups = [];

    if (orderState) {
      bnlUnlockedGroups = this.cloneArray(this.props.cart.bnlUnlockedGroups);

      orderState.cartItems.forEach((cartItem) => {
        if (cartItem.lock_type === 'unlocker') {
          cartItem.lock_group.forEach((set) => {
            if (!bnlUnlockedGroups.includes(set)) bnlUnlockedGroups.push(set);
          });
        }
      });
    }

    this.props.updateBnlUnlockedGroups(bnlUnlockedGroups);
  }

  submitForm(event) {
    event.preventDefault();

    const { eventType, eventCategory } = this.props.additionalData;

    // If using config-driven form, use config validation
    if (this.state.useConfigDrivenForm) {
      if (!this.validateConfigForm()) {
        return;
      }
    } else {
      // Use existing hardcoded validation
      if (
        eventCategory !== 'sports' &&
        eventType === 'catch_all' &&
        !this.state.schoolName
      ) {
        Swal.fire('Please enter the School Name');
        return;
      } else if (eventCategory !== 'sports' && !this.state.grade) {
        Swal.fire('Please select a grade');
        return;
      } else if (eventCategory === 'sports' && !this.state.coachName) {
        Swal.fire("Please enter the Coach's name");
        return;
      } else if (
        eventCategory === 'sports' &&
        eventType === 'catch_all' &&
        !this.state.leagueName
      ) {
        Swal.fire('Please enter the League Name');
        return;
      } else if (
        eventCategory === 'sports' &&
        STUDIOS_REQUIRING_TEAM_NAMES.includes(this.props.company.name) &&
        !this.state.teamName
      ) {
        Swal.fire('Please enter the Team Name');
        return;
      }
    }

    // Continue with the rest of the submission logic
    const that = this;
    const {
      additionalData: { keySubmittedByUser },
      bnlPreShoot,
      setPersonalizationProduct,
    } = this.props;
    const student = bnlPreShoot.selectedStudent;
    const studentId = bnlPreShoot.selectedStudent.id;
    const studentName = `${bnlPreShoot.selectedStudent.firstName} ${bnlPreShoot.selectedStudent.lastName}`;
    const { shootKey } = bnlPreShoot;

    const details = {
      age: this.state.age,
      coachName: this.state.coachName,
      grade: this.state.grade,
      homeRoom: this.state.homeRoom,
      leagueName: this.state.leagueName,
      schoolName: this.state.schoolName,
      teacherSalutation: this.state.teacherSalutation,
      teacherLastName: this.state.teacherLastName,
      teacherName: this.state.teacherName || this.state.teacherLastName,
      teamName: this.state.teamName,
    };

    // Merge config-driven fields into details so they are persisted
    if (this.state.useConfigDrivenForm) {
      const activeFields = this.getActiveFields();
      activeFields.forEach((field) => {
        details[field.state_key] = this.state[field.state_key] || null;
      });
    }

    const data = {
      student_id: bnlPreShoot.selectedStudent.id,
      shoot_key: bnlPreShoot.shootKey,
      event_id: bnlPreShoot.shootId,
      grade_code: this.state.grade,
      key_submitted_by_user: keySubmittedByUser,
      coach_name: this.state.coachName,
      team_name: this.state.teamName,
      age: this.state.age,
    };

    const { grade } = this.state;
    const salutation = this.state.teacherSalutation || 'None';
    const lastName = this.state.teacherLastName || 'None';
    const schoolName = this.state.schoolName || 'None';
    const leagueName = this.state.leagueName || 'None';
    const homeRoom = this.state.homeRoom || 'None';
    const coachName = this.state.coachName || 'None';
    const teamName = this.state.teamName || 'None';
    const age = this.state.age || 'None';

    let alertTitle =
      'Please verify your student info is exactly how it would appear in school records';
    let alertHtml = '';

    // Generate alert HTML based on config or existing logic
    if (this.state.useConfigDrivenForm && this.state.formConfig) {
      // Config-driven alert generation
      const activeFields = this.getActiveFields();
      const configSubjectName =
        this.getSubjectName() ||
        (eventCategory === 'sports' ? 'athlete' : 'student');
      const capitalizedSubjectName =
        configSubjectName.charAt(0).toUpperCase() + configSubjectName.slice(1);

      alertTitle = `Please verify your ${configSubjectName} info is correct`;
      alertHtml = `<b>${capitalizedSubjectName} Name</b>: ${studentName}<br><b>Event Key</b>: ${shootKey}<br>`;

      activeFields.forEach((field) => {
        const value = this.state[field.state_key] || 'None';
        alertHtml += `<b>${field.label}</b>: ${value}<br>`;
      });
    } else {
      // Existing hardcoded alert generation
      alertHtml = `<b>Student Name</b>: ${studentName}<br> <b>School Key</b>: ${shootKey}<br> <b>Grade</b>: ${grade}<br> <b>Teacher Salutation</b>: ${salutation}<br> <b>Teacher Last Name</b>: ${lastName}<br> <b>Home Room</b>: ${homeRoom}`;

      if (eventType === 'catch_all') {
        alertHtml = `<b>School Name</b>: ${schoolName}<br> <b>Student Name</b>: ${studentName}<br> <b>School Key</b>: ${shootKey}<br> <b>Grade</b>: ${grade}<br> <b>Teacher Salutation</b>: ${salutation}<br> <b>Teacher Last Name</b>: ${lastName}<br> <b>Home Room</b>: ${homeRoom}`;
      }

      if (eventCategory === 'sports') {
        alertTitle = 'Please verify your athlete info is correct';
        if (eventType === 'catch_all') {
          alertHtml = `<b>Athlete Name</b>: ${studentName}<br> <b>Event Key</b>: ${shootKey}<br> <b>Coach Name</b>: ${coachName}<br> <b>League Name</b>: ${leagueName} <br> <b>Team Name</b>: ${teamName}<br> <b>Age</b>: ${age}`;
        } else {
          alertHtml = `<b>Athlete Name</b>: ${studentName}<br> <b>Event Key</b>: ${shootKey}<br> <b>Coach Name</b>: ${coachName}<br> <b>Team Name</b>: ${teamName}<br> <b>Age</b>: ${age}`;
        }
      }
    }

    Swal.fire({
      title: alertTitle,
      html: alertHtml,
      showCancelButton: true,
      reverseButtons: true,
      focusConfirm: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'OK',
    }).then((res) => {
      if (res.value) {
        that.setState({ loading: true }, () => {
          that.props.setBnlPreShootStudentsDetails(studentId, details);
          that.props.getShootData(data).then((resp) => {
            that.setState({ loading: false }, () => {
              if (!resp.payload.data.errors) {
                let isGeskus = false;

                // Check for prepay form config from API response
                if (resp.payload.data.prepay_form_config) {
                  that.setState({
                    formConfig: resp.payload.data.prepay_form_config,
                    useConfigDrivenForm:
                      resp.payload.data.prepay_form_config.enabled === true,
                  });
                }

                that.formatBackgroundOptions(
                  resp.payload.data.contains_background_categories,
                  resp.payload.data.background_options,
                );
                that.props.setRetouchingProduct(
                  resp.payload.data.retouching_product,
                );
                that.props.setRetouchingProducts(
                  resp.payload.data.retouching_products,
                );

                setPersonalizationProduct(
                  resp.payload.data.personalization_product,
                );

                that.props.setInterstitialProducts(
                  resp.payload.data.interstitial_products,
                );

                that.props.setPreCartOfferProducts(
                  resp.payload.data.pre_cart_offer_products,
                );

                that.props.setTriggersPreCartOfferProducts(
                  resp.payload.data.triggers_pre_cart_offer_products,
                );

                that.formatIncentiveProducts(
                  resp.payload.data.incentive_products,
                );

                if (
                  resp.payload.data.shoot.company_auth_token.toLowerCase() ===
                  'geskus'
                ) {
                  isGeskus = true;
                }

                that.props.setAdditionalData({
                  isGeskus,
                  selectedStudent: student,
                  shootKey: bnlPreShoot.shootKey,
                  student,
                  studentId: student.id,
                });

                that.setUnlockedGroups(studentId);
                this.props.history.push('/v3/incentive-offers');
              }
            });
          });
        });
      }
    });
  }

  getFieldSet() {
    const { formConfig } = this.state;
    if (!formConfig?.field_sets) return null;

    const { eventCategory } = this.props.additionalData || {};
    const fieldSetKey = eventCategory || 'default';
    return (
      formConfig.field_sets[fieldSetKey] ||
      formConfig.field_sets['default'] ||
      null
    );
  }

  getActiveFields() {
    const { formConfig } = this.state;
    if (!formConfig) return [];

    const { eventCategory, eventType } = this.props.additionalData || {};

    let fields;

    if (formConfig.field_sets) {
      const fieldSet = this.getFieldSet();
      // Support both formats:
      // - Object with 'fields' key: { subject_name: 'athlete', fields: [...] }
      // - Direct array (legacy): [...]
      if (Array.isArray(fieldSet)) {
        fields = fieldSet;
      } else {
        fields = fieldSet?.fields || [];
      }
    } else {
      fields = formConfig.fields || [];
    }

    // Filter by show_when conditions
    return fields.filter((field) => {
      if (!field.show_when) return true;
      const context = {
        event_type: eventType,
        event_category: eventCategory,
      };
      return Object.entries(field.show_when).every(
        ([key, value]) => context[key] === value,
      );
    });
  }

  getSubjectName() {
    const { formConfig } = this.state;
    if (!formConfig) return null;

    // Check field_set-level override first, then company-level
    if (formConfig.field_sets) {
      const fieldSet = this.getFieldSet();
      if (fieldSet && !Array.isArray(fieldSet) && fieldSet.subject_name) {
        return fieldSet.subject_name;
      }
    }

    if (formConfig.subject_name) {
      return formConfig.subject_name;
    }

    return null;
  }

  renderGradeDropDown() {
    const options = [
      'SELECT GRADE',
      'Pre-K Full',
      'Pre-K AM',
      'Pre-K PM',
      'K-Full',
      'K-AM',
      'K-PM',
      '1',
      '2',
      '3',
      '4',
      '5',
      '6',
      '7',
      '8',
      '9',
      '10',
      '11',
      '12',
    ];

    return (
      <div>
        <label>Grade (required)</label>
        <Dropdown
          className="order-shoot-key-form__dropdown"
          options={options}
          value={this.state.grade}
          name="grade"
          handleOnChange={this.handleOnChange}
        />
      </div>
    );
  }

  renderTeacherSalutationDropDown() {
    const options = ['SELECT SALUTATION', 'Mr.', 'Mrs.', 'Ms.', 'Dr.'];

    return (
      <div>
        <label>Teacher Salutation (optional)</label>
        <Dropdown
          className="order-shoot-key-form__dropdown"
          options={options}
          value={this.state.teacherSalutation}
          name="teacherSalutation"
          handleOnChange={this.handleOnChange}
        />
      </div>
    );
  }

  renderConfigField(field) {
    const {
      state_key: stateKey,
      type,
      name,
      label,
      required,
      options,
      placeholder,
    } = field;

    if (type === 'select') {
      const selectOptions = options || [];
      // Find the label for the current value
      const currentOption = selectOptions.find(
        (opt) => opt.value === this.state[stateKey],
      );
      const displayValue = currentOption
        ? currentOption.label
        : selectOptions[0]?.label || '';

      return (
        <div key={name}>
          <label>
            {label} {required ? '(required)' : '(optional)'}
          </label>
          <Dropdown
            className="order-shoot-key-form__dropdown"
            options={selectOptions.map((opt) => opt.label)}
            value={displayValue}
            name={stateKey}
            handleOnChange={(e) => {
              // Find the option by label and get its value
              const selectedOption = selectOptions.find(
                (opt) =>
                  opt.label === e.target.value || opt.value === e.target.value,
              );
              if (selectedOption) {
                this.setState({ [stateKey]: selectedOption.value });
              }
            }}
          />
        </div>
      );
    }

    return (
      <div key={name}>
        <label>
          {label} {required ? '(required)' : '(optional)'}
        </label>
        <input
          className="bnl-dashboard__pre-shoot-form__form__input"
          name={stateKey}
          onChange={this.handleOnChange}
          onKeyPress={this.handleKeyPress}
          placeholder={placeholder || ''}
          value={this.state[stateKey] || ''}
        />
      </div>
    );
  }

  validateConfigForm() {
    const activeFields = this.getActiveFields();
    if (!activeFields || activeFields.length === 0) return true;

    for (const field of activeFields) {
      if (field.required && !this.state[field.state_key]) {
        if (field.type === 'select') {
          Swal.fire(`Please select a ${field.label.toLowerCase()}`);
        } else {
          Swal.fire(`Please enter the ${field.label}`);
        }
        return false;
      }
    }
    return true;
  }

  renderConfigDrivenForm() {
    const { formConfig } = this.state;
    const additionalData = this.props.additionalData || {};
    const bnlPreShoot = this.props.bnlPreShoot || {};
    const student = bnlPreShoot.selectedStudent || {};
    const { eventCategory } = additionalData;

    const subjectName =
      this.getSubjectName() ||
      (eventCategory === 'sports' ? 'athlete' : 'student');

    return (
      <PreshootDetailsWrapper>
        <h3 className="bnl-dashboard__pre-shoot-form__header">
          Please confirm your {subjectName}'s details
        </h3>
        <div className="bnl-dashboard__pre-shoot-form__subheader">
          We use this information to match your order with your {subjectName}{' '}
          after picture day
        </div>
        <div className="bnl-dashboard__pre-shoot-form__student-name">
          {student.firstName} {student.lastName}
        </div>
        <div className="bnl-dashboard__pre-shoot-form__shoot-key">
          {bnlPreShoot.shootKey || ''}
        </div>
        <div className="bnl-dashboard__pre-shoot-form__shoot-name">
          {bnlPreShoot.shootName || ''}
        </div>
        <form
          className="bnl-dashboard__pre-shoot-form__form"
          onSubmit={this.submitForm}
        >
          {this.getActiveFields().map((field) => this.renderConfigField(field))}
          <input
            className="bnl-dashboard__pre-shoot-form__button button--round-border bg--bnl-light-blue"
            type="submit"
            value="Next"
          />
        </form>
      </PreshootDetailsWrapper>
    );
  }

  renderActiveComponent() {
    // Show skeleton loader while fetching config
    if (this.state.loadingConfig) {
      return (
        <PreshootDetailsWrapper>
          <FormSkeletonLoader />
        </PreshootDetailsWrapper>
      );
    }

    // Use config-driven form if enabled for this company
    if (this.state.useConfigDrivenForm) {
      return this.renderConfigDrivenForm();
    }

    // Otherwise, use the existing hardcoded form logic
    const additionalData = this.props.additionalData;
    const student = this.props.bnlPreShoot.selectedStudent;
    const { eventCategory, eventType } = additionalData;

    let subjectName = 'student';

    if (eventCategory === 'sports') {
      subjectName = 'athlete';
    }

    return (
      <PreshootDetailsWrapper>
        <h3 className="bnl-dashboard__pre-shoot-form__header">
          Please confirm your {subjectName}'s details
        </h3>
        <div className="bnl-dashboard__pre-shoot-form__subheader">
          We use this information to match your order with your {subjectName}{' '}
          after picture day
        </div>
        <div className="bnl-dashboard__pre-shoot-form__student-name">
          {student.firstName} {student.lastName}
        </div>
        <div className="bnl-dashboard__pre-shoot-form__shoot-key">
          {this.props.bnlPreShoot.shootKey}
        </div>
        <div className="bnl-dashboard__pre-shoot-form__shoot-name">
          {this.props.bnlPreShoot.shootName}
        </div>
        <form
          className="bnl-dashboard__pre-shoot-form__form"
          onSubmit={this.submitForm}
        >
          {eventCategory === 'sports' && (
            <>
              <label>Coach Name (required)</label>
              <input
                className="bnl-dashboard__pre-shoot-form__form__input"
                name="coachName"
                onChange={this.handleOnChange}
                onKeyPress={this.handleKeyPress}
                placeholder=""
                value={this.state.coachName}
              />
              {eventType === 'catch_all' && (
                <>
                  <label>League Name (required)</label>
                  <input
                    className="bnl-dashboard__pre-shoot-form__form__input"
                    name="leagueName"
                    onChange={this.handleOnChange}
                    onKeyPress={this.handleKeyPress}
                    placeholder=""
                    value={this.state.leagueName}
                  />
                </>
              )}
              <label>{`Team Name ${
                STUDIOS_REQUIRING_TEAM_NAMES.includes(this.props.company.token)
                  ? '(required)'
                  : '(optional)'
              }`}</label>
              <input
                className="bnl-dashboard__pre-shoot-form__form__input"
                name="teamName"
                onChange={this.handleOnChange}
                onKeyPress={this.handleKeyPress}
                placeholder=""
                value={this.state.teamName}
              />
              <label>Age (optional)</label>
              <input
                className="bnl-dashboard__pre-shoot-form__form__input"
                name="age"
                onChange={this.handleOnChange}
                onKeyPress={this.handleKeyPress}
                placeholder=""
                value={this.state.age}
              />
            </>
          )}
          {eventCategory !== 'sports' && eventType === 'catch_all' && (
            <>
              <label>School Name (required)</label>
              <input
                className="bnl-dashboard__pre-shoot-form__form__input"
                name="schoolName"
                onChange={this.handleOnChange}
                onKeyPress={this.handleKeyPress}
                placeholder=""
                value={this.state.schoolName}
              />
            </>
          )}
          {eventCategory !== 'sports' && this.renderGradeDropDown()}
          {eventCategory !== 'sports' && this.renderTeacherSalutationDropDown()}
          {eventCategory !== 'sports' && (
            <>
              <label>Teacher Last Name (optional)</label>
              <input
                className="bnl-dashboard__pre-shoot-form__form__input"
                name="teacherLastName"
                onChange={this.handleOnChange}
                onKeyPress={this.handleKeyPress}
                placeholder=""
                value={this.state.teacherLastName}
              />
              <label>Home Room (optional)</label>
              <input
                className="bnl-dashboard__pre-shoot-form__form__input"
                name="homeRoom"
                onChange={this.handleOnChange}
                onKeyPress={this.handleKeyPress}
                placeholder=""
                value={this.state.homeRoom}
              />
            </>
          )}
          <input
            className="bnl-dashboard__pre-shoot-form__button button--round-border bg--bnl-light-blue"
            type="submit"
            value="Next"
          />
        </form>
      </PreshootDetailsWrapper>
    );
  }

  render() {
    const loading = this.state.loading ? 'loading' : 'hidden';

    return (
      <div>
        <div className={loading}>
          <div className="spinner" />
        </div>
        {this.renderActiveComponent()}
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    additionalData: state.additionalData,
    bnlPreShoot: state.bnlPreShoot,
    cart: state.cart,
    shoot: state.shoot,
    company: state.company,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      formatAndSetBackgroundOptions,
      formatAndSetIncentiveProducts,
      getShootData,
      setAdditionalData,
      setBnlPreShootStudentsDetails,
      setRetouchingProduct,
      setRetouchingProducts,
      setPersonalizationProduct,
      setInterstitialProducts,
      setPreCartOfferProducts,
      setTriggersPreCartOfferProducts,
      updateBnlUnlockedGroups,
    },
    dispatch,
  );
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(BnlParentStudentPreShootDetailForm);

// TODO: add props validation
